import { devLog, devError } from '../utils/devLogger';
import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Table, Badge, Button, Form, Alert, Spinner, ProgressBar } from 'react-bootstrap';
import { jsPDF } from 'jspdf';
import { autoTable } from 'jspdf-autotable';
import { useAuth } from '../auth/AuthContext';
import { db } from '../firebase';
import { collection, getDocs, query, orderBy, limit } from 'firebase/firestore';
import AdminNavbar from '../components/AdminNavbar';
import '../styles/brand.css';

const AttendanceReports = () => {
  const { currentUser, userProfile } = useAuth();
  const [attendanceData, setAttendanceData] = useState([]);
  const [cohorts, setCohorts] = useState([]);
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterCohort, setFilterCohort] = useState('all');
  const [filterModule, setFilterModule] = useState('all');
  const [dateRange, setDateRange] = useState({
    startDate: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], // 30 days ago
    endDate: new Date().toISOString().split('T')[0] // today
  });
  const [reportData, setReportData] = useState({
    overallAttendance: 0,
    totalSessions: 0,
    avgAttendanceRate: 0,
    cohortStats: {},
    moduleStats: {}
  });

  useEffect(() => {
    if (currentUser && userProfile?.role === 'admin') {
      loadData();
    }
  }, [currentUser, userProfile, dateRange, filterCohort, filterModule]);

  const loadData = async () => {
    try {
      setLoading(true);
      
      // Load cohorts
      const cohortsCollection = collection(db, 'cohorts');
      const cohortsSnapshot = await getDocs(cohortsCollection);
      const cohortsData = cohortsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      // Load modules
      const modulesCollection = collection(db, 'modules');
      const modulesSnapshot = await getDocs(modulesCollection);
      const modulesData = modulesSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      // Load attendance records
      const attendanceCollection = collection(db, 'attendance');
      let attendanceQuery = query(attendanceCollection, orderBy('date', 'desc'), limit(500));
      
      const attendanceSnapshot = await getDocs(attendanceQuery);
      const attendanceRecords = attendanceSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        date: doc.data().date?.toDate?.() || doc.data().markedAt?.toDate?.() || new Date(doc.data().date || Date.now())
      }));
      
      // Load users for attendance analysis
      const usersCollection = collection(db, 'users');
      const usersSnapshot = await getDocs(usersCollection);
      const users = usersSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      // Filter attendance records by date range
      const startDate = new Date(dateRange.startDate);
      const endDate = new Date(dateRange.endDate);
      const filteredAttendance = attendanceRecords.filter(record => 
        record.date >= startDate && record.date <= endDate
      );
      
      // Process attendance data
      const processedData = processAttendanceData(filteredAttendance, users, cohortsData, modulesData);
      
      setCohorts(cohortsData);
      setModules(modulesData);
      setAttendanceData(processedData.attendanceRecords);
      setReportData(processedData.stats);
      
    } catch (error) {
      devError('Error loading attendance data:', error);
    } finally {
      setLoading(false);
    }
  };

  const processAttendanceData = (attendanceRecords, users, cohorts, modules) => {
    const learners = users.filter(user => user.role === 'learner');
    const cohortStats = {};
    const moduleStats = {};

    const attendedStatuses = new Set(['present', 'excused', 'late']);
    const normaliseDateKey = (date) => {
      if (!date) return 'unknown-date';
      return date.toISOString().split('T')[0];
    };

    // Initialize cohort stats
    cohorts.forEach(cohort => {
      cohortStats[cohort.id] = {
        name: cohort.name,
        totalLearners: learners.filter(l => l.cohortId === cohort.id).length,
        totalSessions: 0,
        totalAttendance: 0,
        totalRecords: 0,
        attendanceRate: 0
      };
    });

    // Initialize module stats
    modules.forEach(module => {
      moduleStats[module.id] = {
        name: module.title,
        totalSessions: 0,
        totalAttendance: 0,
        totalRecords: 0,
        attendanceRate: 0
      };
    });

    const sessionMap = new Map();

    attendanceRecords.forEach(record => {
      const status = (record.status || record.attendanceStatus || 'not_marked').toLowerCase();
      const dateKey = normaliseDateKey(record.date);
      const sessionKey = record.sessionId || `${dateKey}-${record.cohortId || 'unknown'}-${record.moduleId || 'unknown'}`;

      if (!sessionMap.has(sessionKey)) {
        sessionMap.set(sessionKey, {
          id: record.sessionId || sessionKey,
          sessionName: record.sessionName || record.sessionTitle || 'Session',
          date: record.date || new Date(dateKey),
          cohortId: record.cohortId || null,
          moduleId: record.moduleId || null,
          duration: record.duration || 0,
          attendeeCount: 0,
          totalInvited: 0,
          attendanceRate: 0
        });
      }

      const session = sessionMap.get(sessionKey);
      session.totalInvited += 1;
      if (attendedStatuses.has(status)) {
        session.attendeeCount += 1;
      }
      if (record.duration && record.duration > session.duration) {
        session.duration = record.duration;
      }
    });

    const processedRecords = Array.from(sessionMap.values()).map(session => {
      const rate = session.totalInvited > 0
        ? Math.round((session.attendeeCount / session.totalInvited) * 100)
        : 0;
      return {
        ...session,
        attendanceRate: rate
      };
    });

    processedRecords.forEach(session => {
      if (session.cohortId && cohortStats[session.cohortId]) {
        cohortStats[session.cohortId].totalSessions += 1;
        cohortStats[session.cohortId].totalAttendance += session.attendeeCount;
        cohortStats[session.cohortId].totalRecords += session.totalInvited;
      }

      if (session.moduleId && moduleStats[session.moduleId]) {
        moduleStats[session.moduleId].totalSessions += 1;
        moduleStats[session.moduleId].totalAttendance += session.attendeeCount;
        moduleStats[session.moduleId].totalRecords += session.totalInvited;
      }
    });

    Object.keys(cohortStats).forEach(cohortId => {
      const stats = cohortStats[cohortId];
      if (stats.totalRecords > 0) {
        stats.attendanceRate = Math.round((stats.totalAttendance / stats.totalRecords) * 100);
      }
    });

    Object.keys(moduleStats).forEach(moduleId => {
      const stats = moduleStats[moduleId];
      if (stats.totalRecords > 0) {
        stats.attendanceRate = Math.round((stats.totalAttendance / stats.totalRecords) * 100);
      }
    });

    const totalAttendance = processedRecords.reduce((sum, record) => sum + record.attendeeCount, 0);
    const totalInvited = processedRecords.reduce((sum, record) => sum + record.totalInvited, 0);
    const overallAttendanceRate = totalInvited > 0 ? Math.round((totalAttendance / totalInvited) * 100) : 0;

    return {
      attendanceRecords: processedRecords,
      stats: {
        overallAttendance: totalAttendance,
        totalSessions: processedRecords.length,
        avgAttendanceRate: overallAttendanceRate,
        cohortStats,
        moduleStats
      }
    };
  };

  const handleDateChange = (field, value) => {
    setDateRange(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const exportToCSV = () => {
    // CSV Headers
    const headers = [
      'Date',
      'Session Name',
      'Cohort',
      'Module',
      'Attendees',
      'Total Invited',
      'Attendance Rate (%)'
    ];
    
    // CSV Data
    const csvData = [];
    csvData.push(headers.join(','));
    
    attendanceData.forEach(record => {
      const cohortName = cohorts.find(c => c.id === record.cohortId)?.name || 'N/A';
      const moduleName = modules.find(m => m.id === record.moduleId)?.title || 'N/A';
      
      const row = [
        record.date.toISOString().split('T')[0],
        record.sessionName || 'Session',
        cohortName,
        moduleName,
        record.attendeeCount,
        record.totalInvited,
        record.attendanceRate
      ];
      csvData.push(row.join(','));
    });
    
    // Create and download CSV file
    const csvContent = csvData.join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    
    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `Attendance_Report_${dateRange.startDate}_to_${dateRange.endDate}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const exportToPdfByCohort = () => {
    const doc = new jsPDF();
    const rangeLabel = `${dateRange.startDate} to ${dateRange.endDate}`;

    const cohortsById = cohorts.reduce((acc, cohort) => {
      acc[cohort.id] = cohort.name;
      return acc;
    }, {});

    const modulesById = modules.reduce((acc, module) => {
      acc[module.id] = module.title;
      return acc;
    }, {});

    const sessionsByCohort = attendanceData.reduce((acc, record) => {
      const cohortId = record.cohortId || 'unknown';
      if (!acc[cohortId]) {
        acc[cohortId] = [];
      }
      acc[cohortId].push(record);
      return acc;
    }, {});

    const cohortEntries = Object.entries(sessionsByCohort);
    if (cohortEntries.length === 0) {
      return;
    }

    cohortEntries.forEach(([cohortId, records], index) => {
      if (index > 0) {
        doc.addPage();
      }

      const cohortName = cohortsById[cohortId] || 'Unknown Cohort';
      doc.setFontSize(16);
      doc.text('Attendance Report', 14, 18);
      doc.setFontSize(11);
      doc.text(`Cohort: ${cohortName}`, 14, 26);
      doc.setFontSize(10);
      doc.text(`Range: ${rangeLabel}`, 14, 32);

      const rows = records.map((record) => ([
        record.date ? record.date.toISOString().split('T')[0] : '',
        record.sessionName || 'Session',
        modulesById[record.moduleId] || 'N/A',
        record.attendeeCount,
        record.totalInvited,
        `${record.attendanceRate}%`
      ]));

      autoTable(doc, {
        startY: 38,
        head: [[
          'Date',
          'Session',
          'Module',
          'Attendees',
          'Total Invited',
          'Attendance Rate'
        ]],
        body: rows,
        styles: { fontSize: 8 }
      });
    });

    doc.save(`attendance_report_by_cohort_${dateRange.startDate}_to_${dateRange.endDate}.pdf`);
  };

  const getAttendanceRateBadge = (rate) => {
    if (rate >= 90) return <Badge bg="success">{rate}%</Badge>;
    if (rate >= 75) return <Badge bg="info">{rate}%</Badge>;
    if (rate >= 60) return <Badge bg="warning">{rate}%</Badge>;
    return <Badge bg="danger">{rate}%</Badge>;
  };

  if (userProfile?.role !== 'admin') {
    return (
      <Container className="mt-5">
        <Alert variant="danger">
          <h4>Access Denied</h4>
          <p>You do not have permission to access attendance reports.</p>
        </Alert>
      </Container>
    );
  }

  if (loading) {
    return (
      <>
        <AdminNavbar currentPage="attendance-reports" />
        <Container className="mt-4">
          <div className="text-center my-5">
            <Spinner animation="border" role="status">
              <span className="visually-hidden">Loading...</span>
            </Spinner>
            <p className="mt-2">Loading attendance reports...</p>
          </div>
        </Container>
      </>
    );
  }

  return (
    <>
      <AdminNavbar currentPage="attendance-reports" />

      {/* Hero Section */}
      <section className="hero-section">
        <Container>
          <Row>
            <Col lg={8}>
              <h1 className="mb-3">Attendance Reports</h1>
              <p className="mission-text mb-4">
                Track and analyze session attendance across cohorts and modules
              </p>
              <p className="mb-0">
                Monitor attendance patterns, generate comprehensive reports, and ensure
                optimal learning engagement across all training programs.
              </p>
            </Col>
          <Col lg={4} className="d-flex align-items-center justify-content-end">
              <div className="d-flex gap-2">
                <Button variant="outline-light" onClick={exportToCSV}>
                  Export CSV
                </Button>
                <Button variant="outline-light" onClick={exportToPdfByCohort}>
                  Export PDF (By Cohort)
                </Button>
              </div>
          </Col>
          </Row>
        </Container>
      </section>

      <Container className="mt-4">

        {/* Overall Statistics */}
        <Row className="mb-4">
          <Col md={3}>
            <Card className="text-center">
              <Card.Body>
                <h3 className="text-primary">{reportData.totalSessions}</h3>
                <small className="text-muted">Total Sessions</small>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="text-center">
              <Card.Body>
                <h3 className="text-success">{reportData.overallAttendance}</h3>
                <small className="text-muted">Total Attendance</small>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="text-center">
              <Card.Body>
                <h3 className="text-info">{reportData.avgAttendanceRate}%</h3>
                <small className="text-muted">Avg Attendance Rate</small>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="text-center">
              <Card.Body>
                <h3 className="text-warning">{cohorts.length}</h3>
                <small className="text-muted">Active Cohorts</small>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Filters */}
        <Row className="mb-4">
          <Col md={12}>
            <Card>
              <Card.Header>
                <h5>Report Filters</h5>
              </Card.Header>
              <Card.Body>
                <Row>
                  <Col md={3}>
                    <Form.Group className="mb-3">
                      <Form.Label>Start Date</Form.Label>
                      <Form.Control
                        type="date"
                        value={dateRange.startDate}
                        onChange={(e) => handleDateChange('startDate', e.target.value)}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={3}>
                    <Form.Group className="mb-3">
                      <Form.Label>End Date</Form.Label>
                      <Form.Control
                        type="date"
                        value={dateRange.endDate}
                        onChange={(e) => handleDateChange('endDate', e.target.value)}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={3}>
                    <Form.Group className="mb-3">
                      <Form.Label>Filter by Cohort</Form.Label>
                      <Form.Select 
                        value={filterCohort} 
                        onChange={(e) => setFilterCohort(e.target.value)}
                      >
                        <option value="all">All Cohorts</option>
                        {cohorts.map(cohort => (
                          <option key={cohort.id} value={cohort.id}>{cohort.name}</option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  <Col md={3}>
                    <Form.Group className="mb-3">
                      <Form.Label>Filter by Module</Form.Label>
                      <Form.Select 
                        value={filterModule} 
                        onChange={(e) => setFilterModule(e.target.value)}
                      >
                        <option value="all">All Modules</option>
                        {modules.map(module => (
                          <option key={module.id} value={module.id}>{module.title}</option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Cohort Attendance Stats */}
        <Row className="mb-4">
          <Col md={6}>
            <Card>
              <Card.Header>
                <h5>Attendance by Cohort</h5>
              </Card.Header>
              <Card.Body>
                <Table size="sm">
                  <thead>
                    <tr>
                      <th>Cohort</th>
                      <th>Sessions</th>
                      <th>Attendance Rate</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.values(reportData.cohortStats).map((cohort, index) => (
                      <tr key={index}>
                        <td>{cohort.name}</td>
                        <td>
                          <Badge bg="secondary">{cohort.totalSessions}</Badge>
                        </td>
                        <td>
                          <div className="mb-1">
                            {getAttendanceRateBadge(cohort.attendanceRate)}
                          </div>
                          <ProgressBar 
                            now={cohort.attendanceRate} 
                            variant={cohort.attendanceRate >= 75 ? 'success' : 'warning'}
                            size="sm"
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </Card.Body>
            </Card>
          </Col>
          <Col md={6}>
            <Card>
              <Card.Header>
                <h5>Attendance by Module</h5>
              </Card.Header>
              <Card.Body>
                <Table size="sm">
                  <thead>
                    <tr>
                      <th>Module</th>
                      <th>Sessions</th>
                      <th>Attendance Rate</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.values(reportData.moduleStats).slice(0, 8).map((module, index) => (
                      <tr key={index}>
                        <td>
                          <small>{module.name}</small>
                        </td>
                        <td>
                          <Badge bg="secondary">{module.totalSessions}</Badge>
                        </td>
                        <td>
                          <div className="mb-1">
                            {getAttendanceRateBadge(module.attendanceRate)}
                          </div>
                          <ProgressBar 
                            now={module.attendanceRate} 
                            variant={module.attendanceRate >= 75 ? 'success' : 'warning'}
                            size="sm"
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Detailed Attendance Records */}
        <Card>
          <Card.Header>
            <div className="d-flex justify-content-between align-items-center">
              <h5>Session Attendance Details</h5>
              <Badge bg="info">{attendanceData.length} sessions</Badge>
            </div>
          </Card.Header>
          <Card.Body>
            <Table striped bordered hover responsive>
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Session</th>
                  <th>Cohort</th>
                  <th>Module</th>
                  <th>Attendees</th>
                  <th>Attendance Rate</th>
                </tr>
              </thead>
              <tbody>
                {attendanceData.map((record) => {
                  const cohortName = cohorts.find(c => c.id === record.cohortId)?.name || 'N/A';
                  const moduleName = modules.find(m => m.id === record.moduleId)?.title || 'N/A';
                  
                  return (
                    <tr key={record.id}>
                      <td>
                        <small>{record.date.toLocaleDateString()}</small>
                      </td>
                      <td>
                        <strong>{record.sessionName || 'Session'}</strong>
                      </td>
                      <td>
                        <Badge bg="primary">{cohortName}</Badge>
                      </td>
                      <td>
                        <small>{moduleName}</small>
                      </td>
                      <td>
                        <Badge bg="success">{record.attendeeCount}</Badge>
                        <span className="text-muted">/{record.totalInvited}</span>
                      </td>
                      <td>
                        {getAttendanceRateBadge(record.attendanceRate)}
                      </td>
                    </tr>
                  );
                })}
                {attendanceData.length === 0 && (
                  <tr>
                    <td colSpan="6" className="text-center text-muted py-4">
                      No attendance records found for the selected date range and filters.
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </Card.Body>
        </Card>
      </Container>
    </>
  );
};

export default AttendanceReports;
